import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import Footer from '../components/Footer';
import LanguageSwitcher from '../components/LanguageSwitcher/LanguageSwitcher';
import MetaHead from '../components/MetaHead';
import { storage } from '../utils/platform';
import { API_BASE } from '../config/api';
import { translateApiError } from '../utils/errors';
import { isNative } from '../utils/platform';

// ⚠️ Logique fonctionnelle 100% préservée (i18n P4 — pas de changement de comportement) :
//  - useState email/password/error/loading
//  - useEffect redirect /dashboard si token + gestion ?force=1
//  - handleLogin : POST /api/auth/login → parse défensif → storage.set('token') → /owner si isOwner sinon /dashboard
//  - Message resetSuccess (?reset=success), submit Enter, liens "Mot de passe oublié" et "Créer un compte"

export default function Login() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetSuccess = searchParams.get('reset') === 'success';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await storage.get('token');
      if (cancelled) return;
      if (!token) return;
      if (searchParams.get('force') === '1') {
        await storage.remove('token');
        return;
      }
      navigate('/dashboard');
    })();
    return () => { cancelled = true; };
  }, [navigate, searchParams]);

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      // Parse défensif : un 502/504 Railway peut renvoyer du HTML.
      let data = {};
      try { data = await res.json(); } catch {}

      // 429 → message de rate-limit explicite (sinon confondu avec 401 → "mdp incorrect").
      // i18n P6 : translateApiError gère les codes backend, fallback hardcode pour rate-limit
      // car le backend renvoie 429 sans error code spécifique.
      if (res.status === 429) {
        setError(t('login.error_rate_limit'));
        setLoading(false);
        return;
      }
      // Autres erreurs HTTP (500, 503, etc.) : translateApiError décode le code backend.
      if (!res.ok && res.status !== 401) {
        setError(translateApiError(data) || t('login.error_server', { status: res.status }));
        setLoading(false);
        return;
      }

      if (data.token) {
        await storage.set('token', data.token);
        // Owner Revyo (Florian / Adrien) → directement sur le dashboard owner,
        // pas sur le dashboard restaurateur. Voir backend/routes/auth.js /login.
        navigate(data.user?.isOwner ? '/owner' : '/dashboard');
      } else {
        // i18n P6 : préfère le code backend ('invalid_credentials') s'il est présent
        // → le helper produit un message localisé selon i18n.language en cours.
        setError(translateApiError(data) || t('login.error_invalid_credentials'));
      }
    } catch {
      setError(t('login.error_connection'));
    }
    setLoading(false);
  };

  return (
    <>
      <MetaHead page="auth_login" />
      <AuroraBackground />
      <style>{css}</style>
      <div className="auth-page">
        {!isNative() && (
        <header className="auth-nav">
          <Link to="/" className="auth-nav-logo">
            <div className="auth-logo-square">R</div>
            <span className="auth-logo-name">Revyo</span>
          </Link>
          <div className="auth-nav-right">
            <LanguageSwitcher compact />
            <Link to="/register" className="auth-nav-cta">{t('common.nav_register')}</Link>
          </div>
        </header>
        )}

        <main className="auth-split">
          <div className="auth-form-col">
            <div className="auth-form-inner">
              <h1 className="auth-title">{t('login.title')}</h1>
              <p className="auth-subtitle">{t('login.subtitle')}</p>

              {resetSuccess && (
                <div className="auth-success">{t('login.reset_success')}</div>
              )}
              {error && <div className="auth-error" role="alert">{error}</div>}

              <div className="auth-field">
                <label htmlFor="login-email">{t('login.email_label')}</label>
                <input
                  id="login-email"
                  type="email"
                  placeholder={t('login.email_placeholder')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>

              <div className="auth-field">
                <div className="auth-label-row">
                  <label htmlFor="login-password">{t('login.password_label')}</label>
                  <Link to="/forgot-password" className="auth-link-forgot">{t('login.forgot_password')}</Link>
                </div>
                <input
                  id="login-password"
                  type="password"
                  placeholder={t('login.password_placeholder')}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                  autoComplete="current-password"
                />
              </div>

              <button className="auth-btn" onClick={handleLogin} disabled={!email || !password || loading}>
                {loading ? t('login.submitting') : t('login.submit')}
              </button>

              <p className="auth-switch">
                {t('login.no_account_line')} <Link to="/register" className="auth-link-primary">{t('login.signup_cta')}</Link>
              </p>
            </div>
          </div>
        </main>
      </div>
      {!isNative() && <Footer />}
    </>
  );
}

// ════════════════════════════════════════════════════════════
// CSS — Dark aurora (Phase 2). Palette alignée AuroraBackground/Navbar/Landing v5.
// ════════════════════════════════════════════════════════════
const css = `
.auth-page{position:relative;z-index:1;min-height:100vh;background:transparent;color:#F5F2EB;font-family:'Inter',system-ui,-apple-system,sans-serif;-webkit-font-smoothing:antialiased;display:flex;flex-direction:column;}

/* ══ Nav minimale contextuelle ══ */
.auth-nav{display:flex;justify-content:space-between;align-items:center;padding:20px 32px;max-width:1200px;margin:0 auto;width:100%;box-sizing:border-box;}
.auth-nav-logo{display:flex;align-items:center;gap:10px;text-decoration:none;color:#F5F2EB;}
.auth-nav-right{display:flex;align-items:center;gap:12px;}
.auth-logo-square{width:36px;height:36px;background:linear-gradient(135deg,#E8502A,#C73E1D);color:#FFF;border-radius:9px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:20px;box-shadow:0 4px 12px -2px rgba(232,80,42,.5);}
.auth-logo-name{font-weight:700;font-size:19px;letter-spacing:-0.3px;color:#F5F2EB;}
.auth-nav-cta{background:transparent;color:#F5F2EB;padding:10px 18px;border-radius:999px;text-decoration:none;font-weight:600;font-size:14px;border:1px solid rgba(237,234,227,.14);transition:all .15s ease;}
.auth-nav-cta:hover{background:rgba(255,255,255,.05);border-color:rgba(237,234,227,.28);}

/* ══ Layout centré une colonne ══ */
.auth-split{flex:1;display:flex;justify-content:center;align-items:center;max-width:1200px;margin:0 auto;padding:24px 32px 64px;width:100%;box-sizing:border-box;}

.auth-form-col{display:flex;align-items:center;justify-content:center;width:100%;min-height:calc(100vh - 180px);}
.auth-form-inner{background:rgba(255,255,255,.025);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-radius:24px;padding:44px 44px;width:100%;max-width:440px;border:1px solid rgba(237,234,227,.14);box-shadow:0 40px 80px -20px rgba(0,0,0,.4);box-sizing:border-box;}
.auth-title{font-family:'Sora','Inter',sans-serif;font-size:28px;font-weight:700;letter-spacing:-0.6px;line-height:1.15;margin:0 0 8px;color:#F5F2EB;}
.auth-subtitle{font-size:15px;color:rgba(245,242,235,.62);margin:0 0 28px;line-height:1.5;}

/* ══ Messages succès / erreur dark ══ */
.auth-success{background:rgba(46,160,90,.15);color:#7BE0A8;padding:12px 16px;border-radius:10px;font-size:13px;margin-bottom:16px;font-weight:600;border:1px solid rgba(46,160,90,.3);}
.auth-error{background:rgba(232,80,42,.12);color:#FFB199;padding:12px 16px;border-radius:10px;font-size:13px;margin-bottom:16px;border:1px solid rgba(232,80,42,.4);}

/* ══ Champs ══ */
.auth-field{margin-bottom:16px;}
.auth-label-row{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
.auth-field label{display:block;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:rgba(245,242,235,.62);margin-bottom:8px;}
.auth-label-row label{margin-bottom:0;}
.auth-field input{width:100%;padding:13px 16px;border:1.5px solid rgba(237,234,227,.14);border-radius:10px;font-size:15px;font-family:inherit;color:#F5F2EB;outline:none;transition:border-color .15s,box-shadow .15s,background .15s;background:rgba(255,255,255,.04);box-sizing:border-box;color-scheme:dark;}
.auth-field input::placeholder{color:rgba(245,242,235,.4);}
.auth-field input:focus{border-color:#E8502A;box-shadow:0 0 0 4px rgba(232,80,42,.15);background:rgba(255,255,255,.06);}
.auth-field input:-webkit-autofill,
.auth-field input:-webkit-autofill:hover,
.auth-field input:-webkit-autofill:focus{-webkit-text-fill-color:#F5F2EB;-webkit-box-shadow:0 0 0 1000px rgba(19,17,15,.95) inset;transition:background-color 5000s ease-in-out 0s;caret-color:#F5F2EB;}

.auth-link-forgot{font-size:12px;color:rgba(245,242,235,.62);text-decoration:none;font-weight:500;transition:color .15s;}
.auth-link-forgot:hover{color:#FF8A3D;}

/* ══ CTA principal — gradient orange ══ */
.auth-btn{width:100%;padding:15px;background:linear-gradient(135deg,#E8502A 0%,#FF6B3D 100%);color:#FFF;border:none;border-radius:10px;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit;margin-top:8px;transition:all .15s;box-shadow:0 10px 30px -8px rgba(232,80,42,.55),inset 0 1px 0 rgba(255,255,255,.2);}
.auth-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 14px 36px -8px rgba(232,80,42,.65),inset 0 1px 0 rgba(255,255,255,.2);}
.auth-btn:disabled{opacity:.45;cursor:not-allowed;box-shadow:none;transform:none;}

.auth-switch{text-align:center;margin-top:22px;font-size:14px;color:rgba(245,242,235,.62);}
.auth-link-primary{color:#FF8A3D;text-decoration:none;font-weight:600;}
.auth-link-primary:hover{text-decoration:underline;}

@media (max-width:768px){
  .auth-nav{padding:16px 20px;}
  .auth-nav-cta{padding:8px 14px;font-size:13px;}
  .auth-split{padding:16px 20px 32px;}
  .auth-form-col{min-height:auto;}
  .auth-form-inner{padding:30px 24px;max-width:none;}
  .auth-title{font-size:24px;}
  .auth-subtitle{font-size:14px;margin-bottom:22px;}
}
`;
